import {
	IUnitDetailsDto,
	MachineNameContainerSizeDto,
	RolesType,
	UnitManufacturer,
	UnitModel,
	UnitPickupSystem,
	UnitType,
	UnitWasteType,
} from 'api/api';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { Routes } from 'state/ducks/routes';
import { localized, localizedDynamic } from 'state/i18n';
import { LabelValuePair } from 'types/label-value-pair';
import { images } from 'utilities/images';
import { biConvertNumberToSeparatedString } from 'utilities/number-helpers';
import { hasAtLeast } from 'utilities/roles-util';
import BiCheckbox from 'view/shared/components/bi-checkbox/bi-checkbox';
import { getDateToString } from 'view/shared/components/date-to-string';
import {
	addTranslationLabelValueMapToList,
	getLabelValueOrDefaultToLocalizedNoDataString,
	LabelValueForCardMap,
} from 'view/shared/components/label-value-card';
import { withBleTagSourceIndicator } from './ble-tag-source-indicator';
import { TechnicalDetailsProps } from './technical-details';

export const getUnitTypeText = (unitTypes?: UnitType[], unitTypeId?: number): string => {
	if (!unitTypes) return ' ';
	const unitType = unitTypes.find(a => a.id === unitTypeId);
	return unitType?.nameTranslationKey || ' ';
};

export const getManufacturerText = (manufacturers?: UnitManufacturer[], manufacturerId?: number): string => {
	if (!manufacturers) return ' ';
	const manufacturer = manufacturers.find(a => a.id === manufacturerId);
	if (!manufacturer) return ' ';

	return manufacturer.name !== 'Other Manufacturer' ? manufacturer.name! : localizedDynamic('otherManufacturer');
};

export const getPickupSystem = (pickupSystems?: UnitPickupSystem[], pickupSystemId?: number) => {
	const pickupSystemOptions: LabelValuePair<string | number>[] = (pickupSystems || []).map(element => ({
		label: element.nameTranslationKey ? localizedDynamic(element.nameTranslationKey) : '',
		value: element.id,
	}));

	const pickupSystemSelected = pickupSystemOptions.find(a => a.value === pickupSystemId) ?? {
		label: ' ',
		value: ' ',
	};
	return { pickupSystemSelected, pickupSystemOptions };
};

export const getWasteTypeSelected = (wasteTypes?: UnitWasteType[], wasteTypeId?: number) => {
	const wasteTypesSelectedOptions = (wasteTypes || []).map(element => ({
		label: element.nameTranslationKey ? localizedDynamic(element.nameTranslationKey) : '',
		value: element.id,
		isDeleted: element.isDeleted,
	}));

	const wasteTypeSelected = wasteTypesSelectedOptions.find(a => a.value === wasteTypeId) ?? {
		label: ' ',
		value: ' ',
	};
	const wasteTypesOptions = wasteTypesSelectedOptions
		.filter(e => !e.isDeleted)
		.sort((a, b) => (a.label > b.label ? 1 : -1));

	return { wasteTypeSelected, wasteTypesOptions };
};

export const getUnitModel = (unitModels?: UnitModel[], unitModelId?: number) => {
	const unitModelOptions: LabelValuePair<string | number>[] = (unitModels || []).map(element => ({
		label: element.name ? localizedDynamic(element.name) : '',
		value: element.id,
	}));

	const unitModelSelected = unitModelOptions.find(a => a.value === unitModelId) ?? { label: ' ', value: ' ' };
	return { unitModelSelected, unitModelOptions };
};

export const getContainerSize = (
	unitModels?: UnitModel[],
	unitModelId?: number,
	containerSizes?: MachineNameContainerSizeDto[],
	selectedSize?: number
) => {
	const containerSizeOptions: LabelValuePair<string | number>[] = [];
	const currUnitModelName = unitModels?.find(model => model.id === unitModelId)?.name;

	const matchedContainer = containerSizes?.find(size => size.machineName === currUnitModelName);

	if (matchedContainer && matchedContainer.containerSize) {
		matchedContainer.containerSize
			.sort((a: number, b: number) => a - b)
			.forEach((size: number) => {
				containerSizeOptions.push({ label: `${size} m3`, value: size.toString() });
			});
	}

	const containerSizeSelected = containerSizeOptions.find(a => a.value === selectedSize?.toString()) ?? {
		label: ' ',
		value: ' ',
	};
	return { containerSizeSelected, containerSizeOptions };
};

export const getOptimizationProjectRole = (optimizationProjectEnabledFor: RolesType) => {
	const options: LabelValuePair<string | number>[] = [
		{ label: localized('BasicUser'), value: RolesType.BasicUser.toString() },
		{ label: localized('Admin'), value: RolesType.Admin.toString() },
		{ label: localized('CountryAdmin'), value: RolesType.CountryAdmin.toString() },
		{ label: localized('superAdmin'), value: RolesType.SuperAdmin.toString() },
	].sort((a, b) => Number(a.value) - Number(b.value));

	const selected = options.find(x => x.value === optimizationProjectEnabledFor?.toString()) ?? {
		label: ' ',
		value: ' ',
	};

	return { selected, options };
};

export interface TechnicalDetailsCallbacks {
	toggleIsFavourite: () => void;
	saveItem: <K extends keyof IUnitDetailsDto>(key: K, value: IUnitDetailsDto[K]) => Promise<void>;
	saveItemBasicUser: <K extends keyof IUnitDetailsDto>(
		key: K,
		value: IUnitDetailsDto[K],
		valueType?: 'string' | 'number'
	) => Promise<void>;
}

export const getTechnicalDetailsPairValues = (props: TechnicalDetailsProps, callbacks: TechnicalDetailsCallbacks) => {
	const retMap: LabelValueForCardMap = {
		labels: [],
		valuesForLabels: [],
	};

	const { wasteTypeSelected, wasteTypesOptions } = getWasteTypeSelected(props.wasteTypes, props.wasteTypeId);
	const { pickupSystemSelected, pickupSystemOptions } = getPickupSystem(props.pickupSystem, props.pickupSystemId);
	const { unitModelSelected, unitModelOptions } = getUnitModel(props.unitModels, props.unitModelId);
	const { containerSizeSelected, containerSizeOptions } = getContainerSize(
		props.unitModels,
		props.unitModelId,
		props.containerSizes,
		props.containerSize
	);
	const {
		selected: optimizationProjectRoleSelected,
		options: optimizationProjectRoleOptions,
	} = getOptimizationProjectRole(props.optimizationProjectEnabledFor);

	const unitTypeText = localizedDynamic(getUnitTypeText(props.unitTypes, props.unitTypeId));
	const manufacturerText = getManufacturerText(props.manufacturer, props.manufacturerId);
	const favouriteClassNames = `row-icon ${props.isCountryAdmin ? 'cursor-pointer' : ''}`;

	addTranslationLabelValueMapToList(
		'Favourite',
		<img
			className={favouriteClassNames}
			src={props.isFavourite ? images.favoriteFilled : images.favoriteOutline}
			alt={localized('Favourite')}
			onClick={callbacks.toggleIsFavourite}
		/>,
		retMap
	);

	addTranslationLabelValueMapToList(
		'unitId',
		getLabelValueOrDefaultToLocalizedNoDataString(props.machineDetailsId),
		retMap
	);

	addTranslationLabelValueMapToList(
		'unitModel',
		getLabelValueOrDefaultToLocalizedNoDataString(unitModelSelected.label),
		retMap,
		props.isCountryAdmin,
		value => callbacks.saveItem('unitModelId', value),
		'dropdown',
		unitModelSelected.value,
		unitModelOptions
	);

	if (!props.isBleTag) {
		addTranslationLabelValueMapToList(
			'containerSize',
			getLabelValueOrDefaultToLocalizedNoDataString(containerSizeSelected.label),
			retMap,
			props.isAdmin,
			value => callbacks.saveItem('containerSize', value),
			'dropdown',
			containerSizeSelected.value,
			containerSizeOptions
		);
	}

	const wasteTypeLabel = getLabelValueOrDefaultToLocalizedNoDataString(wasteTypeSelected.label);
	const wasteTypeValue = withBleTagSourceIndicator(
		wasteTypeLabel,
		props.fieldsFromConnectedBleTag?.includes('wasteTypeId') ?? false,
		props.connectedBleTagMac,
		'WasteTypeFromBleTagDeviceInt'
	);

	// The lock deliberately reads connectedBleTagMac, not fieldsFromConnectedBleTag: the former is set
	// whenever a tag is connected, and combined with isSwapContainer is the same condition the API uses to
	// refuse the write. fieldsFromConnectedBleTag answers the narrower "was this value actually replaced by
	// the tag's own data" (it drives the indicator only) and can be false even for a tag-owned field, when
	// the tag's own record left that field blank - switching the lock to it would make the field editable
	// while the API still silently drops the save.
	addTranslationLabelValueMapToList(
		'wasteType',
		wasteTypeValue,
		retMap,
		props.isAdmin && !(props.isSwapContainer && !!props.connectedBleTagMac),
		value => callbacks.saveItem('wasteTypeId', value),
		'dropdown',
		wasteTypeSelected.value,
		wasteTypesOptions
	);

	if (!props.isBleTag) {
		addTranslationLabelValueMapToList(
			'lastestChange',
			props.lastestChange ? getDateToString(props.lastestChange) : '',
			retMap,
			props.isCountryAdmin,
			value => callbacks.saveItem('lastestChange', value),
			'datePicker',
			props.lastestChange
		);

		addTranslationLabelValueMapToList(
			'pickupSystem',
			getLabelValueOrDefaultToLocalizedNoDataString(pickupSystemSelected.label),
			retMap,
			props.isAdmin,
			value => callbacks.saveItem('unitPickupSystemId', value),
			'dropdown',
			pickupSystemSelected.value,
			pickupSystemOptions
		);

		addTranslationLabelValueMapToList(
			'unitsEmptied',
			biConvertNumberToSeparatedString(props.emptyCounter) || '',
			retMap
		);
		addTranslationLabelValueMapToList(
			'totalRunningHours',
			biConvertNumberToSeparatedString(props.totalHourCount) || '',
			retMap
		);
		addTranslationLabelValueMapToList(
			'totalNumberOfCycles',
			biConvertNumberToSeparatedString(props.totalNumberOfCycles) || '',
			retMap
		);
		addTranslationLabelValueMapToList(
			'cyclesSinceLastOutput',
			biConvertNumberToSeparatedString(props.cyclesSinceLastOutput) || '',
			retMap
		);
		addTranslationLabelValueMapToList(
			'pressSeries',
			biConvertNumberToSeparatedString(props.totalNumberOfpressSeries) || '',
			retMap
		);
		addTranslationLabelValueMapToList(
			'pressSeriesSinceLastOutput',
			biConvertNumberToSeparatedString(props.pressSeriesSinceLastOutput) || '',
			retMap
		);

		if (props.unitTracker) {
			addTranslationLabelValueMapToList(
				'trackerBatteryLevel',
				props.unitTracker.batteryStatus
					? `${biConvertNumberToSeparatedString(props.unitTracker.batteryStatus)} %`
					: '-',
				retMap
			);
		}

		addTranslationLabelValueMapToList(
			'devicePhoneNo',
			getLabelValueOrDefaultToLocalizedNoDataString(props.devicePhoneNo),
			retMap,
			props.isAdmin,
			value => callbacks.saveItem('devicePhoneNo', value)
		);
	}

	addTranslationLabelValueMapToList('unitType', getLabelValueOrDefaultToLocalizedNoDataString(unitTypeText), retMap);
	addTranslationLabelValueMapToList(
		'manufacturer',
		getLabelValueOrDefaultToLocalizedNoDataString(manufacturerText),
		retMap
	);

	if (!props.isBleTag) {
		addTranslationLabelValueMapToList(
			'PricePerEmptying',
			props.pricePerEmptying,
			retMap,
			hasAtLeast(props.userRole, props.optimizationProjectEnabledFor),
			value => callbacks.saveItemBasicUser('pricePerEmptying', value, 'number')
		);

		addTranslationLabelValueMapToList(
			'PricePerEmptyingCurrency',
			props.pricePerEmptyingCurrency,
			retMap,
			hasAtLeast(props.userRole, props.optimizationProjectEnabledFor),
			value => callbacks.saveItemBasicUser('pricePerEmptyingCurrency', value)
		);
	}

	if (props.isCountryAdmin) {
		addTranslationLabelValueMapToList(
			'OptimizationProjectEnabledFor',
			getLabelValueOrDefaultToLocalizedNoDataString(optimizationProjectRoleSelected.label),
			retMap,
			props.isSuperAdmin ||
				(props.isCountryAdmin && optimizationProjectRoleSelected.value !== RolesType.SuperAdmin.toString()),
			value => callbacks.saveItem('optimizationProjectEnabledFor', value),
			'dropdown',
			optimizationProjectRoleSelected.value,
			optimizationProjectRoleOptions
		);
	}

	// Compactor-only: "Swap container" row is an admin-editable toggle. The tag's serial links to
	// its own details page whenever a BLE tag is connected, even if swap-container mode is off.
	if (!props.isBleTag) {
		const connectedTagLink =
			props.connectedBleTagMac ? (
				<NavLink to={`${Routes.MachineDetails}/${encodeURIComponent(props.connectedBleTagMac)}`}>
					{props.connectedBleTagMac}
				</NavLink>
			) : null;

		const swapContainerValue = props.isAdmin ? (
			<span className="flex-direction-row flex-center-column flex-space-between-row">
				<BiCheckbox
					checked={!!props.isSwapContainer}
					onChange={e => callbacks.saveItem('isSwapContainer', e.checked)}
				/>
				{connectedTagLink}
			</span>
		) : (
			<span className="flex-direction-row flex-center-column flex-space-between-row">
				{localized(props.isSwapContainer ? 'Yes' : 'No')}
				{connectedTagLink}
			</span>
		);

		addTranslationLabelValueMapToList('SwapContainer', swapContainerValue, retMap);

		addTranslationLabelValueMapToList(
			'BleTagConnectionStatus',
			localized(props.connectedBleTagMac ? 'Connected' : 'Disconnected'),
			retMap
		);
	} else {
		// BLE tag: the same status seen from the tag's side, linking to the container it is connected to.
		addTranslationLabelValueMapToList(
			'BleTagConnectionStatus',
			<span className="flex-direction-row flex-center-column flex-space-between-row">
				{localized(props.connectedContainerSerialNumber ? 'Connected' : 'Disconnected')}
				{props.connectedContainerSerialNumber && (
					<NavLink
						to={`${Routes.MachineDetails}/${encodeURIComponent(props.connectedContainerSerialNumber)}`}
					>
						{props.connectedContainerSerialNumber}
					</NavLink>
				)}
			</span>,
			retMap
		);
	}

	return retMap;
};
